import { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { format, isValid } from 'date-fns';

import { Box, Card, Icon, IconButton, Menu, Tag, Text, Tooltip } from '@zillow/constellation-10';
import {
  IconDeleteOutline,
  IconEditFilled,
  IconListBulletedOutline,
  IconMoreFilled,
} from '@zillow/constellation-icons';

import { PartialMessage } from '@zg-rentals/backend-styled-components';
import { FeeTypeGrouping } from '@zg-rentals/ts-rental-manager-api-client';

import { useGetListingDetails } from '../../../../../hooks/queries/properties';

import CostsandFeesDeleteFeeModal from '../../../../modals/CostsAndFeesDeleteFeeModal';
import MultifamilyViewFeeDetailsModal from '../../../../modals/MultifamilyViewFeeDetailsModal';

import {
  emitClickEditFeeEvent,
  emitClickViewFeeDetailsEvent,
  emitMultifamilyClickDeleteFeeEvent,
  EVENT_IDS,
  TRIGGER_SOURCES,
} from '../../../../../analytics/events/multiFamilyCostsAndFees';
import { useAuth } from '../../../../AuthProvider';

import type React from 'react';
import type {
  GetMultifamilyFeesDictionaryItemsDto,
  MultifamilyFeeDto,
  PartialMessageTextAndUrlDto,
} from '@zg-rentals/ts-rental-manager-api-client';

// Constants
const PRESSED_STATE_DELAY_MS = 300;

type FeeListItemProps = {
  fee: MultifamilyFeeDto;
  dictionaryItems: GetMultifamilyFeesDictionaryItemsDto;
  handleEditFeeClick: (fee: MultifamilyFeeDto) => void;
};

type FeeDisplayObject = {
  feeCategory: string;
  feeName: string;
  feePaymentFrequencyType: string;
  feeRequirementType: string;
  feeRefundableType: string;
  feeCalculationType: string;
  feeAmountDisplay: React.ReactNode;
  feeCreated: string;
};

const getFeeAmountDisplay = (partialMessages: Array<PartialMessageTextAndUrlDto>) => {
  return <PartialMessage fontType="body-sm" partialMessages={partialMessages} />;
};

const getFeeNameDisplay = (fee: MultifamilyFeeDto, dictionaryItems: GetMultifamilyFeesDictionaryItemsDto) => {
  let feeName =
    fee?.feeCategory === FeeTypeGrouping.CUSTOM
      ? fee?.feeName
      : dictionaryItems?.feeCategory[fee?.feeCategory]?.feeType[fee?.feeType]?.displayName;
  // feeDisplayName overides feeName for manual or imported fees
  if (fee?.isImportedFromFeed && fee?.feeDisplayName) {
    feeName = fee?.feeDisplayName;
  }

  return feeName;
};

export const FeeListItem: React.FC<FeeListItemProps> = ({ fee, dictionaryItems, handleEditFeeClick }) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const [isDeleteFeeModalOpen, setIsDeleteFeeModalOpen] = useState(false);
  const [isViewDetailsModalOpen, setIsViewDetailsModalOpen] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const pressedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const displayObject: FeeDisplayObject = {
    feeCategory: dictionaryItems?.feeCategory[fee?.feeCategory]?.displayName ?? '',
    feeName: getFeeNameDisplay(fee, dictionaryItems) ?? '',
    feePaymentFrequencyType: dictionaryItems?.feePaymentFrequencyType[fee?.feePaymentFrequencyType]?.displayName ?? '',
    feeRequirementType: dictionaryItems?.feeRequirementType[fee?.feeRequirementType]?.displayName ?? '',
    feeRefundableType: dictionaryItems?.feeRefundableType[fee?.feeRefundableType]?.displayName ?? '',
    feeCalculationType: dictionaryItems?.feeCalculationType[fee?.feeCalculationType]?.displayName ?? '',
    feeAmountDisplay: getFeeAmountDisplay(fee?.styledComponents?.feeCalculationAndFrequencyDisplay),
    feeCreated: isValid(new Date(Number(fee?.feeCreatedMs)))
      ? format(new Date(Number(fee?.feeCreatedMs)), 'MM/dd/yyyy')
      : '',
  };

  const isDisabled =
    !user?.features?.canUpdateListing ||
    listingDetails?.isArchived ||
    listingDetails?.restrictions?.isUpdateListingRestricted ||
    (listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress &&
      listingDetails?.selfServeOnboardingStatus?.isUpdateRestricted);

  const handleEditClick = (
    triggerSource:
      | typeof TRIGGER_SOURCES.BUTTON_TO_EDIT_FEE
      | typeof TRIGGER_SOURCES.VIEW_DETAILS_BUTTON_TO_EDIT_FEE = TRIGGER_SOURCES.BUTTON_TO_EDIT_FEE,
  ) => {
    handleEditFeeClick(fee);
    if (listingDetails && user) {
      emitClickEditFeeEvent({
        currentProperty: listingDetails,
        user,
        triggerSource,
      });
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteFeeModalOpen(true);
    if (listingDetails && user) {
      emitMultifamilyClickDeleteFeeEvent({ currentProperty: listingDetails, user });
    }
  };

  const handleFeeItemClick = () => {
    setIsPressed(true);
    // Clear any existing timeout
    if (pressedTimeoutRef.current) {
      clearTimeout(pressedTimeoutRef.current);
    }
    // Brief delay to show the pressed state before opening modal
    pressedTimeoutRef.current = setTimeout(() => {
      setIsViewDetailsModalOpen(true);
      setIsPressed(false);
      if (listingDetails && user) {
        emitClickViewFeeDetailsEvent({
          currentProperty: listingDetails,
          user,
          triggerSource: TRIGGER_SOURCES.LIST_ITEM_VIEW_FEE_DETAILS,
        });
      }
    }, PRESSED_STATE_DELAY_MS);
  };

  const handleMouseLeave = () => {
    setIsPressed(false);
    if (pressedTimeoutRef.current) {
      clearTimeout(pressedTimeoutRef.current);
    }
  };

  const menu = (
    <Menu.Root>
      <Menu.Trigger>
        <IconButton tone="neutral" emphasis="bare" title="More" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <Icon render={<IconMoreFilled />} />
        </IconButton>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content>
          <Menu.Item
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setIsViewDetailsModalOpen(true);
              if (listingDetails && user) {
                emitClickViewFeeDetailsEvent({
                  currentProperty: listingDetails,
                  user,
                  triggerSource: TRIGGER_SOURCES.BUTTON_TO_VIEW_FEE_DETAILS,
                });
              }
            }}
            data-eventid={EVENT_IDS.CLICK_VIEW_FEE_DETAILS}
          >
            <Menu.ItemIcon render={<IconListBulletedOutline />} />
            <Menu.ItemLabel>View details</Menu.ItemLabel>
          </Menu.Item>
          <Menu.Item
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              handleEditClick();
            }}
            data-eventid={EVENT_IDS.CLICK_EDIT_MF_FEE}
            disabled={isDisabled}
          >
            <Menu.ItemIcon render={<IconEditFilled />} />
            <Menu.ItemLabel>Edit</Menu.ItemLabel>
          </Menu.Item>
          {!fee?.isImportedFromFeed && (
            <Menu.Item
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleDeleteClick();
              }}
              data-eventid={EVENT_IDS.CLICK_DELETE_MF_FEE}
              disabled={isDisabled}
            >
              <Menu.ItemIcon render={<IconDeleteOutline />} />
              <Menu.ItemLabel>Delete</Menu.ItemLabel>
            </Menu.Item>
          )}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );

  return (
    <>
      <Card
        interactive
        elevated={false}
        outlined={false}
        onClick={handleFeeItemClick}
        onMouseLeave={handleMouseLeave}
        data-testid={`FeeListItem-${fee?.feeGroupId}`}
        data-eventid={EVENT_IDS.CLICK_VIEW_FEE_DETAILS}
      >
        <Box css={{ display: 'flex', flexDirection: 'column' }}>
          <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '600' }}>
            <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: '200' }}>
              <Text textStyle="body-bold">{displayObject.feeName}</Text>
              {fee?.isImportedFromFeed && fee?.isEdited && (
                <Text
                  textStyle="body-xs"
                  css={{
                    color: 'text.subtle',
                  }}
                >
                  (Edited)
                </Text>
              )}
            </Box>
            <Box
              css={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '200',
                marginLeft: 'auto',
              }}
            >
              <Tooltip.Root>
                <Tooltip.Trigger>
                  <Tag
                    tone={fee.isImportedFromFeed ? 'gray' : 'aqua'}
                    size="sm"
                    css={{ flexShrink: 0 }}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    {fee.isImportedFromFeed ? 'Imported from feed' : 'Added manually'}
                  </Tag>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content>
                    {fee.isImportedFromFeed ? (
                      <>
                        <div>
                          <b>Feed name:</b> {fee.feeDisplayName || 'Not provided'}
                        </div>
                        <div>
                          <b>Internal code:</b> {fee.feedFeeInternalCode || 'Not provided'}
                        </div>
                      </>
                    ) : (
                      `Created on ${displayObject.feeCreated}`
                    )}
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
              {menu}
            </Box>
          </Box>
          <Box css={{ marginTop: '200', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <Text textStyle="body-sm">{displayObject.feeAmountDisplay}</Text>
            <Text textStyle="body-sm" css={{ paddingLeft: '200', paddingRight: '200' }}>
              |
            </Text>
            <Text textStyle="body-sm">{displayObject.feeRequirementType}</Text>
            {fee?.feeRefundableType && (
              <>
                <Text textStyle="body-sm" css={{ paddingLeft: '200', paddingRight: '200' }}>
                  |
                </Text>
                <Text textStyle="body-sm">{displayObject.feeRefundableType}</Text>
              </>
            )}
          </Box>
        </Box>
      </Card>
      {isDeleteFeeModalOpen && !fee?.isImportedFromFeed && (
        <CostsandFeesDeleteFeeModal
          feeId={fee.feeGroupId}
          setShowDeleteFeeModal={setIsDeleteFeeModalOpen}
          isSingleFamilyFee={false}
        />
      )}
      {isViewDetailsModalOpen && (
        <MultifamilyViewFeeDetailsModal
          onClose={() => setIsViewDetailsModalOpen(false)}
          fee={fee}
          dictionaryItems={dictionaryItems}
          onEditFee={() => handleEditClick(TRIGGER_SOURCES.VIEW_DETAILS_BUTTON_TO_EDIT_FEE)}
          isEditFeeDisabled={isDisabled || false}
        />
      )}
    </>
  );
};
